import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  bulkDeleteTasksApi,
  bulkMoveTasks,
  createTaskApi,
  deleteTaskApi,
  getManagerTasks,
  mergeTasksApi,
  setTaskState,
  updateTaskApi,
} from '../services/api/tasks';
import type { CreateTaskInput, ProjectWithTasks, UpdateTaskInput } from '../services/api/tasks';

/**
 * Hook to fetch tasks grouped by managed projects
 */
export function useManagerTasks(scope?: 'all') {
  return useQuery({
    queryKey: ['managerTasks', scope ?? 'managed'],
    queryFn: () => getManagerTasks(scope),
  });
}

/**
 * Hook to bulk move tasks to a target project
 */
export function useBulkMoveTasks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskIds, targetProjectId }: { taskIds: number[]; targetProjectId: number }) =>
      bulkMoveTasks(taskIds, targetProjectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managerTasks'] });
    },
  });
}

/**
 * Hook to create a task
 */
export function useCreateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTaskInput) => createTaskApi(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managerTasks'] });
    },
  });
}

/**
 * Hook to update a task
 */
export function useUpdateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateTaskInput }) => updateTaskApi(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managerTasks'] });
    },
  });
}

/**
 * Hook to delete a single task
 */
export function useDeleteTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteTaskApi(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managerTasks'] });
    },
  });
}

/**
 * Hook to bulk delete tasks
 */
export function useBulkDeleteTasks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskIds: number[]) => bulkDeleteTasksApi(taskIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managerTasks'] });
    },
  });
}

/**
 * Hook to merge source tasks into a target task
 */
export function useMergeTasks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sourceTaskIds, targetTaskId }: { sourceTaskIds: number[]; targetTaskId: number }) =>
      mergeTasksApi(sourceTaskIds, targetTaskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managerTasks'] });
    },
  });
}

/**
 * Change a task's state with optimistic UI update (Phase 19, D-20).
 *
 * Flow:
 *   1. onMutate: cancel ['managerTasks'] in-flight, snapshot the cache,
 *      patch the matching task's `taskStateId` immediately so the chip
 *      re-renders before the server responds.
 *   2. onError: restore the snapshot. The consumer (TaskStateChip — plan
 *      19-05) reads `mutation.error` and displays an AppToast (D-21).
 *   3. onSettled: invalidate ['managerTasks'] so the canonical row from
 *      the server replaces the optimistic patch.
 *
 * No retry — PATCH is idempotent server-side, but masking bugs is worse
 * than visibility (D-20).
 */
export function useSetTaskState() {
  const queryClient = useQueryClient();
  return useMutation<
    { taskId: number; taskStateId: number },
    Error,
    { projectId: number; taskId: number; taskStateId: number },
    { previous: ProjectWithTasks[] | undefined }
  >({
    mutationFn: ({ projectId, taskId, taskStateId }) =>
      setTaskState(projectId, taskId, taskStateId),
    onMutate: async ({ projectId, taskId, taskStateId }) => {
      await queryClient.cancelQueries({ queryKey: ['managerTasks'] });
      const previous = queryClient.getQueryData<ProjectWithTasks[]>(['managerTasks']);
      if (previous) {
        queryClient.setQueryData<ProjectWithTasks[]>(
          ['managerTasks'],
          previous.map((project) =>
            project.id === projectId
              ? {
                  ...project,
                  tasks: project.tasks.map((task) =>
                    task.id === taskId ? { ...task, taskStateId } : task
                  ),
                }
              : project
          )
        );
      }
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(['managerTasks'], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['managerTasks'] });
    },
  });
}
